import { useEffect, useCallback, useRef } from "react"
import { Link } from "react-router-dom"
import { useQuery, useSubscription } from "urql"
import { Radio, Target, Zap, AlertTriangle, Link2, Send, Clock, CheckCircle2, XCircle, Mail, Package, ArrowUpCircle, Layers, Users, Activity } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { SyncedNumberTicker } from "@/components/shared/synced-number-ticker"
import { BorderBeam } from "@/components/ui/border-beam"
import { StatusQuery } from "@/lib/graphql/queries"
import { MessageReceivedSubscription, OutboundDeliveryUpdatedSubscription, OutboundDLQEntryCreatedSubscription } from "@/lib/graphql/outbound"
import {
  EventReceivedSubscription,
  DLQEntryCreatedSubscription,
  SourceChangedSubscription,
  DestinationChangedSubscription,
  SubscriptionChangedSubscription,
  DeliveryUpdatedSubscription,
} from "@/lib/graphql/subscriptions"
import { useRefetchOnFocus } from "@/hooks/use-refetch-on-focus"
import { useAuth } from "@/hooks/use-auth"
import { cn } from "@/lib/utils"

interface StatCardProps {
  label: string
  value: number | undefined
  displayValue?: string
  icon: React.ReactNode
  iconColor: string
  loading: boolean
  alert?: boolean
  to?: string
}

function StatCard({ label, value, displayValue, icon, iconColor, loading, alert, to }: StatCardProps) {
  const content = (
    <>
      <div className={cn("rounded-lg p-2.5", iconColor)}>
        {icon}
      </div>
      <div>
        {loading ? (
          <Skeleton className="h-8 w-16 mb-1" />
        ) : displayValue ? (
          <div className="text-xl font-bold tabular-nums">{displayValue}</div>
        ) : (
          <div className="text-3xl font-bold tabular-nums">
            <SyncedNumberTicker value={value ?? 0} />
          </div>
        )}
        <div className="text-xs text-muted-foreground">{label}</div>
      </div>
      {alert && (
        <BorderBeam
          size={40}
          duration={4}
          colorFrom="#ef4444"
          colorTo="#f59e0b"
        />
      )}
    </>
  )

  if (to) {
    return (
      <Link
        to={to}
        className={cn(
          "relative rounded-lg glass-card p-5 flex items-center gap-4 overflow-hidden",
          "animate-fade-in-up hover:ring-1 hover:ring-border transition-shadow cursor-pointer",
        )}
      >
        {content}
      </Link>
    )
  }

  return (
    <div
      className={cn(
        "relative rounded-lg glass-card p-5 flex items-center gap-4 overflow-hidden",
        "animate-fade-in-up",
      )}
    >
      {content}
    </div>
  )
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

export function StatsCards() {
  const { isConfigured } = useAuth()
  const [status, reexecute] = useQuery({ query: StatusQuery })

  const [eventSub] = useSubscription({ query: EventReceivedSubscription, pause: !isConfigured })
  const [dlqSub] = useSubscription({ query: DLQEntryCreatedSubscription, pause: !isConfigured })
  const [sourceSub] = useSubscription({ query: SourceChangedSubscription, pause: !isConfigured })
  const [destSub] = useSubscription({ query: DestinationChangedSubscription, pause: !isConfigured })
  const [subSub] = useSubscription({ query: SubscriptionChangedSubscription, pause: !isConfigured })
  const [deliverySub] = useSubscription({ query: DeliveryUpdatedSubscription, pause: !isConfigured })
  const [msgSub] = useSubscription({ query: MessageReceivedSubscription, pause: !isConfigured })
  const [outDeliverySub] = useSubscription({ query: OutboundDeliveryUpdatedSubscription, pause: !isConfigured })
  const [outDlqSub] = useSubscription({ query: OutboundDLQEntryCreatedSubscription, pause: !isConfigured })

  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasMounted = useRef(false)
  const debouncedRefetch = useCallback(() => {
    if (refetchTimer.current) clearTimeout(refetchTimer.current)
    refetchTimer.current = setTimeout(() => {
      reexecute({ requestPolicy: "network-only" })
    }, 300)
  }, [reexecute])

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    debouncedRefetch()
  }, [
    eventSub.data, dlqSub.data, sourceSub.data, destSub.data, subSub.data,
    deliverySub.data, msgSub.data, outDeliverySub.data, outDlqSub.data,
    debouncedRefetch,
  ])

  useRefetchOnFocus(reexecute)

  const s = status.data?.status
  const loading = !s && status.fetching
  const dlqSize = s?.dlqSize ?? 0
  const outboundDlqSize = s?.outboundDlqSize ?? 0

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Inbound</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            label="Sources"
            value={s?.sourcesTotal}
            icon={<Radio className="h-5 w-5 text-blue-400" />}
            iconColor="bg-blue-500/15"
            loading={loading}
            to="/sources"
          />
          <StatCard
            label="Destinations"
            value={s?.destinationsTotal}
            icon={<Target className="h-5 w-5 text-violet-400" />}
            iconColor="bg-violet-500/15"
            loading={loading}
            to="/destinations"
          />
          <StatCard
            label="Subscriptions"
            value={s?.subscriptionsTotal}
            icon={<Link2 className="h-5 w-5 text-cyan-400" />}
            iconColor="bg-cyan-500/15"
            loading={loading}
            to="/subscriptions"
          />
          <StatCard
            label="Total Events"
            value={s?.eventsTotal}
            icon={<Zap className="h-5 w-5 text-emerald-400" />}
            iconColor="bg-emerald-500/15"
            loading={loading}
            to="/events"
          />
          <StatCard
            label="Deliveries"
            value={s?.deliveriesTotal}
            icon={<Send className="h-5 w-5 text-pink-400" />}
            iconColor="bg-pink-500/15"
            loading={loading}
            to="/deliveries"
          />
          <StatCard
            label="Pending Deliveries"
            value={s?.deliveriesPending}
            icon={<Clock className="h-5 w-5 text-orange-400" />}
            iconColor="bg-orange-500/15"
            loading={loading}
            to="/deliveries"
          />
          <StatCard
            label="Delivered"
            value={s?.deliveriesDelivered}
            icon={<CheckCircle2 className="h-5 w-5 text-teal-400" />}
            iconColor="bg-teal-500/15"
            loading={loading}
            to="/deliveries"
          />
          <StatCard
            label="Failed Events"
            value={s?.eventsFailed}
            icon={<XCircle className="h-5 w-5 text-red-400" />}
            iconColor="bg-red-500/15"
            loading={loading}
            to="/events"
          />
          <StatCard
            label="DLQ Size"
            value={dlqSize}
            icon={<AlertTriangle className="h-5 w-5 text-amber-400" />}
            iconColor="bg-amber-500/15"
            loading={loading}
            alert={dlqSize > 0}
            to="/dlq"
          />
        </div>
      </div>

      <div>
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Outbound</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard
            label="Messages"
            value={s?.messagesTotal}
            icon={<Mail className="h-5 w-5 text-indigo-400" />}
            iconColor="bg-indigo-500/15"
            loading={loading}
            to="/messages"
          />
          <StatCard
            label="Outbound Deliveries"
            value={s?.outboundDeliveriesTotal}
            icon={<ArrowUpCircle className="h-5 w-5 text-sky-400" />}
            iconColor="bg-sky-500/15"
            loading={loading}
            to="/messages"
          />
          <StatCard
            label="Outbound Pending"
            value={s?.outboundDeliveriesPending}
            icon={<Clock className="h-5 w-5 text-yellow-400" />}
            iconColor="bg-yellow-500/15"
            loading={loading}
            to="/messages"
          />
          <StatCard
            label="Outbound Failed"
            value={s?.outboundDeliveriesFailed}
            icon={<XCircle className="h-5 w-5 text-rose-400" />}
            iconColor="bg-rose-500/15"
            loading={loading}
            to="/messages"
          />
          <StatCard
            label="Outbound DLQ"
            value={outboundDlqSize}
            icon={<Package className="h-5 w-5 text-amber-400" />}
            iconColor="bg-amber-500/15"
            loading={loading}
            alert={outboundDlqSize > 0}
            to="/outbound-dlq"
          />
        </div>
      </div>

      <div>
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">System</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Queue Depth"
            value={s?.queueDepth}
            icon={<Layers className="h-5 w-5 text-slate-400" />}
            iconColor="bg-slate-500/15"
            loading={loading}
            alert={(s?.queueDepth ?? 0) > 100}
          />
          <StatCard
            label="Workers"
            displayValue={s ? `${s.activeWorkers}/${s.totalWorkers}` : undefined}
            value={undefined}
            icon={<Users className="h-5 w-5 text-purple-400" />}
            iconColor="bg-purple-500/15"
            loading={loading}
          />
          <StatCard
            label="Uptime"
            displayValue={s ? formatUptime(s.uptime) : undefined}
            value={undefined}
            icon={<Activity className="h-5 w-5 text-green-400" />}
            iconColor="bg-green-500/15"
            loading={loading}
          />
          <StatCard
            label="Version"
            displayValue={s?.version ?? "—"}
            value={undefined}
            icon={<Zap className="h-5 w-5 text-fuchsia-400" />}
            iconColor="bg-fuchsia-500/15"
            loading={loading}
          />
        </div>
      </div>
    </div>
  )
}
